import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  acceptInvitation as acceptInvitationRequest,
  getCurrentUser,
  login as loginRequest,
  logout as logoutRequest,
  switchBranch as switchBranchRequest,
  type AcceptInvitationPayload,
  type LoginPayload,
} from '@/services/authService';
import { clearAccessToken, getAccessToken } from '@/services/authStorage';
import { setImpersonatedTenantId } from '@/services/impersonationContext';
import { clearActiveTenantId } from '@/services/tenantStorage';
import type { AuthenticatedUser } from '@/types/auth';

interface AuthContextValue {
  user: AuthenticatedUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (payload: LoginPayload) => Promise<void>;
  logout: () => Promise<void>;
  acceptInvitation: (payload: AcceptInvitationPayload) => Promise<void>;
  switchBranch: (branchId: string) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async (): Promise<void> => {
    const token = getAccessToken();
    if (!token) {
      setUser(null);
      return;
    }

    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      clearAccessToken();
      setUser(null);
    }
  }, []);

  useEffect(() => {
    const loadUser = async (): Promise<void> => {
      try {
        await refreshUser();
      } finally {
        setIsLoading(false);
      }
    };

    void loadUser();
  }, [refreshUser]);

  const login = useCallback(async (payload: LoginPayload): Promise<void> => {
    const currentUser = await loginRequest(payload);
    setUser(currentUser);
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    await logoutRequest();
    setUser(null);
    clearActiveTenantId();
    setImpersonatedTenantId(null);
  }, []);

  const acceptInvitation = useCallback(
    async (payload: AcceptInvitationPayload): Promise<void> => {
      const currentUser = await acceptInvitationRequest(payload);
      setUser(currentUser);
    },
    [],
  );

  const switchBranch = useCallback(async (branchId: string): Promise<void> => {
    const updatedUser = await switchBranchRequest(branchId);
    setUser(updatedUser);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated: Boolean(user),
      login,
      logout,
      acceptInvitation,
      switchBranch,
      refreshUser,
    }),
    [acceptInvitation, isLoading, login, logout, refreshUser, switchBranch, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
